import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { PublicationSettingSearchableContentType } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import { NEWSLETTER_FILTERS } from 'utils/newsletters';
import { ContentTypeFilter, ContentTypeFilterType } from '../content-type-filter/ContentTypeFilter';

interface ContentTypeFilterContainer {
  onChange: (value: ContentTypeFilterType[]) => void;
  setFiltersSet: (filtersSet: boolean) => void;
  values: ContentTypeFilterType[];
}

const ROUTE = {
  PAGES: 'pages',
  ISSUES: 'issues',
  COLLECTIONS: 'collections',
  OFF_PLATFORM: 'off-platform',
  NEWSLETTERS: 'newsletters',
  MULTI_COLLECTION: 'multicollection'
};

export const ContentTypeFilterContainer = ({ onChange, setFiltersSet, values }: ContentTypeFilterContainer) => {
  const { pathname } = useLocation();
  const { pageTypeSetting } = usePageTypeSettingsContext();
  const { data: publicationSettings, isLoading: isLoad, fetchStatus } = usePublicationSettings();
  const currentProperty = useConvertedProperty();

  const isLoading = isLoad && fetchStatus !== 'idle';

  const enabledContentTypes = useMemo(
    () => publicationSettings?.publicationSetting.searchableContentTypes ?? [],
    [publicationSettings]
  );
  const defaultContentTypes = useMemo(
    () => publicationSettings?.publicationSetting.defaultContentTypes ?? [],
    [publicationSettings]
  );

  // Enabled QueryConfigs whose names don't already map to a standard content type become
  // additional entries in the Type filter dropdown.
  const queryConfigEntries = useMemo(() => {
    const configs = publicationSettings?.publicationSetting.baseQueryConfigs ?? [];
    return configs.filter(
      (qc) => qc.enabled && !enabledContentTypes.some((t) => t.toLowerCase() === qc.name.toLowerCase())
    );
  }, [publicationSettings, enabledContentTypes]);

  const queryConfigTypes = useMemo(
    () => queryConfigEntries.map((qc) => qc.name as PublicationSettingSearchableContentType),
    [queryConfigEntries]
  );

  // Label overrides: prefer contentTypeLabel, fall back to name
  const queryConfigLabelOverrides = useMemo(
    () => Object.fromEntries(queryConfigEntries.map((qc) => [qc.name, qc.contentTypeLabel ?? qc.name])),
    [queryConfigEntries]
  );

  const queryConfigIconOverrides = useMemo(
    () =>
      Object.fromEntries(
        queryConfigEntries.filter((qc) => qc.iconSvgPath).map((qc) => [qc.name, { path: qc.iconSvgPath as string }])
      ),
    [queryConfigEntries]
  );

  const queryConfigCategoryOverrides = useMemo(
    () =>
      Object.fromEntries(
        queryConfigEntries
          .filter((qc) => qc.category === 'type' || qc.category === 'brand')
          .map((qc) => [qc.name, qc.category as 'type' | 'brand'])
      ),
    [queryConfigEntries]
  );

  const route = pathname.split('/')[2];

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newContentTypes = [...values];
    const value = e.target.value as PublicationSettingSearchableContentType;

    if (!e.target.checked) {
      onChange(newContentTypes.filter((val) => val !== value));
    } else {
      onChange([...newContentTypes, value]);
    }
    setFiltersSet(true);
  };

  const publishStatusValues = useMemo(() => {
    let base: PublicationSettingSearchableContentType[] = [];
    const isAllContentRoute =
      route === ROUTE.COLLECTIONS || route === ROUTE.OFF_PLATFORM || route === ROUTE.MULTI_COLLECTION;

    if (isAllContentRoute) {
      base = enabledContentTypes;
    } else if (route === ROUTE.NEWSLETTERS) {
      // For properties with NEWSLETTER_FILTERS defined (e.g. marketwatch), restrict to defaultContentTypes
      // to exclude brand types (wsj, barrons, etc.) added to searchableContentTypes but not for newsletters.
      const hasNewsletterFilters = !!currentProperty && !!NEWSLETTER_FILTERS[currentProperty];
      base = hasNewsletterFilters
        ? defaultContentTypes.filter((contentType) => enabledContentTypes.includes(contentType))
        : enabledContentTypes;
    } else {
      base =
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        pageTypeSetting?.contentTypes?.filter((publishStatusValue: PublicationSettingSearchableContentType) =>
          enabledContentTypes.includes(publishStatusValue)
        ) ?? [PublicationSettingSearchableContentType.Article];
    }

    return [...base, ...queryConfigTypes];
  }, [currentProperty, defaultContentTypes, enabledContentTypes, pageTypeSetting, route, queryConfigTypes]);

  const displayContentTypeFilter = publishStatusValues.length > 1;

  const visibleDefaultContentTypes = defaultContentTypes.filter((contentType) =>
    publishStatusValues.includes(contentType)
  );
  const isActive =
    visibleDefaultContentTypes.length !== values.length ||
    !visibleDefaultContentTypes.every((contentType) => values.includes(contentType));

  const handleDeselectAll = () => {
    onChange([]);
    setFiltersSet(true);
  };

  const handleSelectAll = () => {
    onChange(publishStatusValues);
    setFiltersSet(true);
  };

  const handleSelectArticleOnly = () => {
    onChange([PublicationSettingSearchableContentType.Article]);
    setFiltersSet(true);
  };

  return displayContentTypeFilter ? (
    <ContentTypeFilter
      handleChange={handleChange}
      handleDeselectAll={handleDeselectAll}
      handleSelectAll={handleSelectAll}
      handleSelectArticleOnly={handleSelectArticleOnly}
      isActive={isActive}
      isLoading={isLoading}
      publishStatusValues={publishStatusValues}
      values={values}
      currentProperty={currentProperty}
      labelOverrides={queryConfigLabelOverrides}
      iconSvgOverrides={queryConfigIconOverrides}
      categoryOverrides={queryConfigCategoryOverrides}
    />
  ) : (
    <></>
  );
};
